import { Button, Menu, MenuItem, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import {
  Form,
  FormStatus,
  FormTemplate,
  FormTemplateType,
  Visit,
  VisitTemplate,
} from 'api/models';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import useQuery from 'hooks/useQuery';
import { mutation, query } from 'operations';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { getFields } from 'routes/Form/utils';
import { ToastSeverity, toastStateSelector } from 'state';
import { GeneralIds, ModelTypes, RoutePaths } from 'types/types';
import { getRoutePath } from 'utils';

export type FormMenuVariant = 'form' | 'sample' | 'report';

function getTemplates(
  templates: FormTemplateType[],
  variant: FormMenuVariant,
  visitTemplate?: VisitTemplate,
) {
  if (variant === 'form')
    return templates.filter(f =>
      visitTemplate?.supportedFormTemplateTypes?.includes(f.id),
    );

  if (variant === 'sample')
    return templates.filter(f =>
      visitTemplate?.supportedSampleFormTemplateTypes?.includes(f.id),
    );

  if (variant === 'report')
    return templates.filter(f =>
      visitTemplate?.supportedReportTemplateTypes?.includes(f.id),
    );
}

function isEmpty(variant: FormMenuVariant, visitTemplate?: VisitTemplate) {
  if (
    variant === 'form' &&
    visitTemplate?.supportedFormTemplateTypes.length === 0
  )
    return true;

  if (
    variant === 'sample' &&
    (!visitTemplate?.supportedSampleFormTemplateTypes ||
      visitTemplate?.supportedSampleFormTemplateTypes?.length === 0)
  )
    return true;

  if (
    variant === 'report' &&
    (!visitTemplate?.supportedReportTemplateTypes ||
      visitTemplate?.supportedReportTemplateTypes?.length === 0)
  )
    return true;

  return false;
}

type AddFormMenuProps = {
  visit: Visit;
  formTemplateTypes: FormTemplateType[];
  disabled?: boolean;
  variant: FormMenuVariant;
  icon: JSX.Element;
};

export default function AddFormMenu({
  visit,
  formTemplateTypes,
  disabled,
  variant,
  icon,
}: AddFormMenuProps) {
  const { visitTemplateId, id: visitId } = visit;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { projectId, patientId } = useParams<GeneralIds>();
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setToast = useSetRecoilState(toastStateSelector);

  const { data: visitTemplate } = useQuery<VisitTemplate>({
    type: ModelTypes.visitTemplate,
    ids: { id: visitTemplateId, projectId },
  });

  const onGoToForm = async (formTemplateTypeId: string) => {
    const { data: formTemplates } = await query<FormTemplate[]>({
      type: ModelTypes.formTemplate,
      ids: {
        projectId,
        formTemplateTypeId,
      },
    });

    // find the highest version of the form template and status published
    const formTemplate = formTemplates
      ?.filter(el => el.status === FormTemplateStatus.Published)
      .sort((a, b) => b.version - a.version)[0];

    try {
      const { data: form } = await mutation<Form>({
        type: ModelTypes.form,
        body: {
          id: '',
          status: FormStatus.New,
          formTemplateId: formTemplate.id,
          fields: getFields(
            {},
            formTemplate.fieldTemplates,
            formTemplate.layout.items,
          ),
        },
        ids: {
          visitId,
          id: '',
        },
      });

      const path = getRoutePath(RoutePaths.formEdit, {
        projectId,
        patientId,
        visitId,
        formTemplateTypeId: form?.formTemplateTypeId,
        formId: form?.id,
      });
      navigate(path);
    } catch (e: any) {
      const errorCode = e.response?.data?.error?.[0].code;
      setToast({
        isOpen: true,
        message: t(`errors.${errorCode}`) ?? t('general.errors.save'),
        severity: ToastSeverity.error,
      });
    }
  };

  const templates = getTemplates(formTemplateTypes, variant, visitTemplate);

  const isEmptyList = isEmpty(variant, visitTemplate);

  return (
    <Box ml={1}>
      <Tooltip
        title={
          visitTemplate?.supportedFormTemplateTypes.length === 0
            ? t('forms.thereAreNoAvailableForms') ?? ''
            : t(`forms.add${variant}`)
        }
      >
        <Button
          id="add-form-menu-button"
          aria-controls={open ? 'add-form-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={event => setAnchorEl(event.currentTarget)}
          size="small"
          disabled={disabled || isEmptyList}
          sx={{
            minWidth: '0px',
            width: '52px',
            height: '32px',
          }}
          variant="text"
        >
          +{icon}
        </Button>
      </Tooltip>
      <Menu
        id="add-form-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          'aria-labelledby': 'add-form-menu-button',
        }}
        sx={{ ml: -6 }}
      >
        {templates?.map(formTemplateType => (
          <MenuItem
            key={formTemplateType.id}
            onClick={() => onGoToForm(formTemplateType.id)}
          >
            {formTemplateType.name}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
